import { forwardRef, Fragment } from 'react'
import type { Attributes, ForwardedRef } from 'react'
import styled from 'styled-components'

import type { components } from '../../constants/schema'

import { Text, StyledText } from '../text'

import { Color } from '../../theme/color'

export function ArtipioContentsHeader({ children }: { children: any }) {
  return <HeaderContainer>{children}</HeaderContainer>
}

function HeaderTitle({ children }: { children: any }) {
  return (
    <Text size={16} weight={500}>
      {children}
    </Text>
  )
}

function HeaderDate({ children }: { children: any }) {
  return (
    <Text size={12} color="text_secondary">
      {children}
    </Text>
  )
}

export const ArtipioContents = Object.assign(
  forwardRef(
    ({ children, isPadding = true }: { children: any; isPadding?: boolean }, ref: ForwardedRef<HTMLDivElement>) => {
      return (
        <ContentContainer ref={ref} isPadding={isPadding}>
          {children}
        </ContentContainer>
      )
    },
  ),
)

export function ArtipioContentItem({
  type,
  content,
  alt,
  isFirstImg,
  isLastImg,
  prop,
  ...props
}: {
  type: components['schemas']['MarkdownContent']['type']
  content: components['schemas']['MarkdownContent']['content'] | 'header'
  prop?: components['schemas']['MarkdownContent']['prop']
  alt?: string
  isFirstImg?: boolean
  isLastImg?: boolean
} & Attributes) {
  const item = () => {
    switch (type) {
      case 'header':
      case 'heading1':
        return (
          <Heading1
            size={24}
            weight={600}
            lineHeight={34}
            color={prop?.color ?? 'text_primary'}
            $textAlign={prop?.align ?? 'initial'}
          >
            {content}
          </Heading1>
        )
      case 'heading2':
        return (
          <Heading2 size={22} weight={600} color={prop?.color ?? 'text_primary'} $textAlign={prop?.align ?? 'initial'}>
            {content}
          </Heading2>
        )
      case 'heading3':
        return (
          <Heading3 size={18} weight={600} color={prop?.color ?? 'text_primary'} $textAlign={prop?.align ?? 'initial'}>
            {content}
          </Heading3>
        )
      case 'body':
        return (
          <Body
            size={14}
            lineHeight={22}
            color={prop?.color ?? 'text_primary'}
            $textAlign={prop?.align ?? 'initial'}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )
      case 'bullet':
        const splitContent = content.split('\n')
        return (
          <Bullet size={14} lineHeight={22}>
            {splitContent.map((content, index) => (
              <Fragment key={index}>
                <span dangerouslySetInnerHTML={{ __html: content }} />
                <br />
              </Fragment>
            ))}
          </Bullet>
        )
      case 'caption':
        return (
          <Caption
            size={12}
            color={prop?.color ?? 'text_secondary'}
            $textAlign={prop?.align ?? 'initial'}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )
      case 'link':
        return (
          <Link href={prop?.link} target="_blank">
            <Text size={14} color="primary_6">
              {content}
            </Text>
          </Link>
        )
      case 'image':
        return (
          <Image
            src={String(content)}
            alt={alt}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = '/img/thumbnail.svg'
            }}
            isFirstImg={isFirstImg}
            isLastImg={isLastImg}
            padding={prop?.shrinkPadding}
          />
        )
      default:
        return <></>
    }
  }

  return <CategoryWrap {...props}>{item()}</CategoryWrap>
}

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 24px;
  padding: 8px 0 16px;
  border-bottom: 1px solid ${Color.line};
`

const ContentContainer = styled.div<{ isPadding?: boolean }>`
  ${({ isPadding }) => (isPadding ? 'padding: 0 24px' : undefined)};
`

const CategoryWrap = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-line;
`

const Heading1 = styled(StyledText)`
  margin: 32px 0 16px;
`

const Heading2 = styled(StyledText)`
  margin: 16px 0 8px;
`

const Heading3 = styled(StyledText)`
  margin: 16px 0 8px;
`

const Body = styled(StyledText)`
  margin: 8px 0;
  > strong {
    font-weight: 700;
  }
`

const Bullet = styled(StyledText)`
  margin: 8px 0;
`

const Caption = styled(StyledText)`
  margin: 8px 0;
`

const Link = styled.a`
  display: inline-block;
  width: fit-content;
`

const Image = styled.img<{ isFirstImg?: boolean; isLastImg?: boolean; padding?: boolean }>`
  max-width: none;
  margin: ${({ isFirstImg }) => (isFirstImg ? 0 : 24)}px 0 ${({ isLastImg }) => (isLastImg ? 0 : 24)}px;
  // TODO: shrinkPadding 관련 코드 수정 필요
  ${({ padding }) =>
    padding
      ? ''
      : `
        width: calc(100% + 24px * 2);
        margin-left: -24px;
      `}
`

ArtipioContentsHeader.Title = HeaderTitle
ArtipioContentsHeader.Date = HeaderDate
